/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IHeiScrollspyViewProps } from './hei-scrollspy';
import useIsInViewport from 'use-is-in-viewport';
import classnames from 'classnames';

type SectionHeadingsProps = {
    sections: React.ReactNode[];
    sectionInViewport: number;
};

const SectionHeadings = ({ sections, sectionInViewport }: SectionHeadingsProps) => {
    const [isStuck, setIsStuck] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const headings = React.useMemo(() => {
        return sections.map(item =>
            React.cloneElement(item as React.ReactElement, {
                titleOnly: true
            })
        );
    }, [sections]);

    React.useEffect(() => {
        const cachedRef = containerRef.current;
        const observer = new IntersectionObserver(
            ([e]) => {
                setIsStuck(e.intersectionRatio < 1);
            },
            { threshold: [1] }
        );
        cachedRef && observer.observe(cachedRef);
        return () => {
            cachedRef && observer.unobserve(cachedRef);
        };
    }, [containerRef]);

    return (
        <div
            className={classnames('hei-scrollspy-headings-container', {
                'is-stuck': isStuck
            })}
            ref={containerRef}
        >
            <div className='height-container'></div>
            <div className='container'>
                <ul className='hei-scrollspy-headings'>
                    <div className='overflow-container'>
                        {headings.map((item, idx) => {
                            return (
                                <li
                                    className={classnames('hei-scrollspy-heading', {
                                        active: sectionInViewport === idx
                                    })}
                                    role='tab'
                                    key={idx}
                                >
                                    {item}
                                </li>
                            );
                        })}
                    </div>
                </ul>
            </div>
        </div>
    );
};

const SectionContent = ({
    children,
    index,
    updateIsInViewport
}: React.PropsWithChildren<{
    index: number;
    updateIsInViewport: (index: number, isInViewport: boolean) => void;
}>) => {
    const [isInViewport, targetRef] = useIsInViewport({
        // account for fixed headers
        modTop: '-160px'
    });

    React.useEffect(() => {
        updateIsInViewport(index, isInViewport ?? false);
    }, [index, isInViewport]);

    return (
        <div className='hei-scrollspy-content container' ref={targetRef}>
            {children}
        </div>
    );
};

export default (props: IHeiScrollspyViewProps) => {
    const {
        config: { className },
        slots: { sections }
    } = props;

    const sectionsInViewport = React.useRef<Record<number, boolean>>({});
    const [sectionInViewport, setSectionInViewport] = React.useState(-1);

    const updateIsInViewport = React.useCallback((index: number, isInViewport: boolean) => {
        // process.env.NODE_ENV === 'development' && console.log('hei-scrollspy', 'updateIsInViewport', index, isInViewport);
        const dict = sectionsInViewport.current;
        dict[index] = isInViewport;

        let firstIdx = -1;
        Object.keys(dict).forEach(idxStr => {
            const idx = +idxStr;
            if (dict[idx] && firstIdx < 0) {
                firstIdx = idx;
                process.env.NODE_ENV === 'development' && console.log('hei-scrollspy', 'active idx', firstIdx);
            }
        });

        setSectionInViewport(firstIdx);
    }, []);

    return (
        <div className={classnames('hei-scrollspy', className)}>
            <SectionHeadings sectionInViewport={sectionInViewport} sections={sections} />
            {sections.map((s, idx) => (
                <SectionContent updateIsInViewport={updateIsInViewport} index={idx} key={idx}>
                    {s}
                </SectionContent>
            ))}
        </div>
    );
};
